import { flattenFilterConfig } from "../../../discovery/FilterModule/utils/flattenFilterConfig";
import { toActiveFilterSource } from "../../../discovery/FilterModule/utils/toActiveFilterSource";

/**
 * ONLY WORKS WITH SELF MANAGED/ AUTO GEN GENERATED FILTERS
 * Handles visualization segment click to add or remove filter
 *
 * @param {Object} params - Parameters object
 * @param {string} params.clickedSegment - The ID of the clicked segment
 * @param {Object} params.activeBlockFilterKey - The filter key of the active block
 * @param {Array} params.activeFilters - Current active filters array
 * @param {Function} params.setActiveFilters - State setter for active filters
 * @param {Array} params.filterConfig - Filter configuration array
 * @param {Object} params.toastActions - Banner context actions { SET_INFO_BANNER, SET_VARIANT }
 * @returns {void}
 */

/**
 * Builds a normalized activeFilter entry matching the shape from
 * FilterModule's convertToOutput (handleOnChange.js).
 */
const buildFilterEntry = (config, values) => ({
  filterId: config.filterId,
  filterKey: config.filterKey,
  target: config.target,
  source: toActiveFilterSource(config.source),
  display: config.display,
  values,
});

export const handleVizSegmentClick = ({
  clickedSegment,
  activeBlockFilterKey,
  activeFilters,
  setActiveFilters,
  filterConfig,
  toastActions,
}) => {
  // Validate required parameters
  if (!activeBlockFilterKey || !filterConfig || !setActiveFilters) {
    console.warn('handleVizSegmentClick: Missing required parameters');
    return;
  }

  // Direct find for flat configs (non-self-managed / unifiedFilterGroup).
  // Fallback to flattenFilterConfig for nested/hierarchy configs (self-managed V3).
  const selectedSectionFilterConfig =
    filterConfig.find((f) => f?.filterKey === activeBlockFilterKey) ||
    flattenFilterConfig(filterConfig).find((f) => f?.filterKey === activeBlockFilterKey);

  if (!selectedSectionFilterConfig) {
    console.warn('handleVizSegmentClick: No filter config found for active block');
    return;
  }

  // Check if this is a numberRangeType filter (e.g., NumberInput blocks)
  const isNumberRangeFilter = selectedSectionFilterConfig?.target?.filterType === 'numberRangeType';

  // Construct filter value directly from clickedSegment (visualization data)
  // clickedSegment is a CountVizLabels object with: name, value (optional), tooltipLabel, etc.
  // Use value if present (tag IDs, etc.), otherwise fall back to name
  const filterValue = clickedSegment.value || clickedSegment.name;
  const filterDisplay = clickedSegment.tooltipLabel || clickedSegment.name || filterValue;

  // For numberRangeType filters, use the range values from the bucket
  if (isNumberRangeFilter && (clickedSegment.rangeStart !== undefined || clickedSegment.rangeEnd !== undefined)) {
    const rangeValues = {
      min: clickedSegment.rangeStart,
      max: clickedSegment.rangeEnd
    };

    // Check if this exact range filter is already applied
    const existingFilter = activeFilters.find(
      (f) => f?.filterKey === selectedSectionFilterConfig?.filterKey
    );
    const isAlreadyApplied = existingFilter?.values?.min === rangeValues.min &&
                            existingFilter?.values?.max === rangeValues.max;

    if (isAlreadyApplied) {
      // Remove the filter
      const updatedFilters = activeFilters.filter(
        (f) => f?.filterKey !== selectedSectionFilterConfig?.filterKey
      );
      setActiveFilters(updatedFilters);

      if (toastActions) {
        const { SET_VARIANT, SET_INFO_BANNER } = toastActions;
        SET_VARIANT?.("toast");
        SET_INFO_BANNER?.({
          msg: `Filter Removed: ${filterDisplay}`,
          actionIcon: "X",
          timeOut: 2000,
        });
      }
    } else {
      // Add or replace the filter with range values
      const updatedFilters = activeFilters.filter(
        (f) => f?.filterKey !== selectedSectionFilterConfig?.filterKey
      );
      updatedFilters.push(buildFilterEntry(selectedSectionFilterConfig, rangeValues));
      setActiveFilters(updatedFilters);

      if (toastActions) {
        const { SET_VARIANT, SET_INFO_BANNER } = toastActions;
        SET_VARIANT?.("toast");
        SET_INFO_BANNER?.({
          msg: `Filter Added: ${filterDisplay}`,
          actionIcon: "X",
          timeOut: 4000,
        });
      }
    }
    return;
  }

  const selectedFilter = [{
    value: filterValue,
    display: filterDisplay
  }];

  // Check if this filter is already applied
  // Compare both by value and by string conversion to handle type mismatches (e.g., ObjectId vs string)
  const isThisFilterAlreadyApplied = activeFilters
    .find((f) => f?.filterKey === selectedSectionFilterConfig?.filterKey)
    ?.values?.some((v) => {
      const vValue = v.value;
      const filterVal = filterValue;
      // Compare both strict equality and string equality to handle type mismatches
      return vValue === filterVal || String(vValue) === String(filterVal);
    });

  if (isThisFilterAlreadyApplied) {
    // Remove the filter
    const updatedFilters = activeFilters
      .map((f) => {
        if (f?.filterKey === selectedSectionFilterConfig?.filterKey) {
          // Filter out the matching value, handling type mismatches
          const updatedValues = f.values.filter((v) => {
            const vValue = v.value;
            const filterVal = filterValue;
            // Compare both strict equality and string equality to handle type mismatches
            return vValue !== filterVal && String(vValue) !== String(filterVal);
          });
          // If no values remain, exclude this filter entirely
          return updatedValues.length > 0 ? { ...f, values: updatedValues } : null;
        }
        return f;
      })
      .filter(Boolean); // Remove null entries

    setActiveFilters(updatedFilters);

    // Show banner notification if available
    if (toastActions) {
      const { SET_VARIANT, SET_INFO_BANNER } = toastActions;
      SET_VARIANT?.("toast");
      SET_INFO_BANNER?.({
        msg: `Filter Removed: ${filterDisplay}`,
        actionIcon: "X",
        timeOut: 2000,
      });
    }
  } else {
    // Add the filter
    const existingFilterIndex = activeFilters.findIndex(
      (f) => f?.filterKey === selectedSectionFilterConfig?.filterKey
    );

    let updatedFilters;
    if (existingFilterIndex !== -1) {
      // Append to existing filter, but prevent duplicates
      const existingFilter = activeFilters[existingFilterIndex];
      const existingValues = existingFilter.values || [];
      
      // prevent duplicates
      const valueAlreadyExists = existingValues.some(
        (v) => v.value === filterValue || String(v.value) === String(filterValue)
      );
      
      if (valueAlreadyExists) {
        // just in case
        console.warn('handleVizSegmentClick: Attempted to add duplicate filter value', {
          filterValue,
          existingValues,
          filterKey: selectedSectionFilterConfig?.filterKey
        });
        return;
      }
      
      updatedFilters = [...activeFilters];
      updatedFilters[existingFilterIndex] = {
        ...existingFilter,
        values: [
          ...existingValues,
          ...selectedFilter,
        ],
      };
    } else {
      // Add new filter
      updatedFilters = [
        ...activeFilters,
        buildFilterEntry(selectedSectionFilterConfig, selectedFilter),
      ];
    }

    setActiveFilters(updatedFilters);

    if (toastActions) {
      const { SET_VARIANT, SET_INFO_BANNER } = toastActions;
      SET_VARIANT?.("toast");
      SET_INFO_BANNER?.({
        msg: `Filter Added: ${filterDisplay}`,
        actionIcon: "X",
        timeOut: 4000,
      });
    }
  }
};